import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  signInAnonymously,
  getIdTokenResult
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  const resolveIsAdmin = async (currentUser) => {
    if (!currentUser || currentUser.isAnonymous) return false;

    try {
      const tokenResult = await getIdTokenResult(currentUser, true);
      return tokenResult?.claims?.admin === true;
    } catch (error) {
      console.error('Erro ao validar permissões admin:', error);
      return false;
    }
  };

  useEffect(() => {
    // Listener para mudanças no estado de autenticação
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        try {
          await signInAnonymously(auth);
          return;
        } catch (error) {
          console.error('Erro ao iniciar sessão anónima:', error);
          setUser(null);
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }
      }

      const adminStatus = await resolveIsAdmin(currentUser);
      setUser(currentUser);
      setIsAdmin(adminStatus);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Login com Firebase Authentication
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const adminStatus = await resolveIsAdmin(userCredential.user);

      if (!adminStatus) {
        await signOut(auth);
        setIsAdmin(false);
        setUser(null);
        return false;
      }

      setIsAdmin(adminStatus);
      setUser(userCredential.user);
      return true;
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      
      return false;
    }
  };

  // Logout com Firebase Authentication
  const logout = async () => {
    try {
      await signOut(auth);
      setIsAdmin(false);
      setUser(null);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const value = {
    isAdmin,
    isLoading,
    user,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
